import React from 'react';
import cn from 'classnames';

export type SegmentOption<T extends string> = {
  value: T;
  label: string;
  ariaLabel?: string;
};

type Props<T extends string> = {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
};

export default function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  size = 'sm',
  className,
  'aria-label': ariaLabel,
}: Props<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex rounded-lg border border-gray-200 bg-slate-100 p-0.5 dark:border-gray-700 dark:bg-gray-800',
        className
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt.ariaLabel || opt.label}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-md font-semibold uppercase tracking-wide transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              size === 'sm' ? 'px-3 py-1.5 text-[11px]' : 'px-4 py-2 text-xs',
              selected
                ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-700 dark:text-blue-400'
                : 'text-slate-500 hover:text-slate-700 dark:text-gray-400 dark:hover:text-gray-200'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
